(function () {

    'use strict';

    // NOTE ensure the namespace and any dependencies are set up appropriately, for both server and client
    if (typeof window === 'undefined') {
        const exports = {};
        channelsFn(
            exports,
            require('../../enums/metrics.js'),
            require('../object.js'),
            require('lodash'),
            require('backbone'),
            require('../../../lib/riq-moment.js')
        );

        module.exports = exports.channels;
    } else {
        RivalIQ.module('data');

        channelsFn(
            RivalIQ.data,
            RivalIQ.enums,
            RivalIQ.Object,
            window._,
            window.Backbone,
            window.moment
        );
    }

    function channelsFn(data, enums, BaseObject, _, Backbone, moment) {
        const Token = Backbone.Model.extend({
            expiresAt: function () {
                const me = this;
                return me.get('expiresAt');
            }
        });

        const Channel = BaseObject.extend({
            idAttribute: 'id',

            getColumns: function () {
                const me = this;

                const engagementSubmetrics = _.map(me.attributes.engagementTypes, function (type) {
                    return {
                        dimensions: ['engagement_type'],
                        engagement_type: type
                    };
                });

                return _.compact(_.flatten([
                    {
                        property: 'engagement_total',
                        submetrics: engagementSubmetrics
                    },
                    me.attributes.properties
                ]));
            },

            getChannel: function () {
                const me = this;

                if (me.isCrossChannel()) {
                    return;
                }

                return me.get('channel');
            },

            getPathName: function () {
                const me = this;

                if (me.isCrossChannel()) {
                    return;
                }

                const channelName = me.get('channelName');
                return (channelName ? channelName.toLowerCase() : me.get('channel'));
            },

            getChannelName: function () {
                const me = this;
                return (me.get('channelName') || me.get('name'));
            },

            getSubChannels: function () {
                const me = this;
                // eslint-disable-next-line no-use-before-define
                return new Channels(me.mapSubChannels(_.identity));
            },

            mapSubChannels: function (fn) {
                const me = this;

                return _.map(me.attributes.subSources, function (source) {
                    const channel = data.channels.get(source);
                    return fn(channel);
                });
            },

            toJSON: function () {
                const me = this;
                return me.attributes;
            },

            get: function (property) {
                const me = this;
                return me.attributes[property];
            },

            set: function (key, value) {
                const me = this;
                me.attributes[key] = value;
            },

            isCrossChannel: function () {
                const me = this;
                return me.get('id') === enums.sources.CROSS_CHANNEL;
            },

            isWeb: function () {
                const me = this;
                return me.get('id') === enums.sources.WEB;
            },

            getTokens: function () {
                const me = this;
                const channel = me.get('channel');

                return _.map(RivalIQ.state.user.get(`${channel}TokenSummary`), function (tokenSummary) {
                    return new Token(tokenSummary);
                });
            },

            // true if there is any unexpired token
            hasConnection: function () {
                const me = this;
                const tokens = me.getTokens();

                if (me.get('multiOauthToken')) {
                    return _.size(tokens) > 0;
                }

                return _.some(tokens, function (token) {
                    const expiresAt = token.expiresAt();
                    return (expiresAt ? moment.utc().isBefore(moment.utc(expiresAt)) : true);
                });
            },

            tokenIsExpired: function (token) {
                const expiresAt = token.get('refreshTokenExpiresAt') || token.get('expiresAt');
                return expiresAt && moment.utc(expiresAt).isBefore(moment.utc());
            },

            getUniqueConnections: function () {
                const me = this;
                const tokens = me.getTokens();
                const userId = RivalIQ.state.user.get('id');
                const accountTokensByUniqKey = _.reduce(_.groupBy(tokens, 'attributes.unique_key_part'), (accountTokens, tokens, uniqKey) => {
                    const token = _.first(tokens);

                    const allTokensExpired = _.every(tokens, me.tokenIsExpired);
                    token.set('expired', allTokensExpired);

                    // mark if the current user own an expired token (we'll redirect them straight to oauth)
                    const userOwnsExpiredToken = _.find(tokens, (token) => {
                        return token.get('account_user_id') === userId && me.tokenIsExpired(token);
                    });
                    token.set('userOwnsExpiredToken', !_.isEmpty(userOwnsExpiredToken));

                    // take the max of the allRefreshTokenExpiresAt and expiresAt since that'll be the most recently conneccted token
                    const allRefreshTokenExpiresAt = _.compact(_.map(tokens, (token) => {
                        const expiresAt = token.get('allRefreshTokenExpiresAt');
                        return expiresAt ? moment.utc(expiresAt) : expiresAt;
                    }));
                    if (_.size(allRefreshTokenExpiresAt)) {
                        token.set('refreshTokenExpiresAt', moment.max(allRefreshTokenExpiresAt));
                    }
                    const allExpiresAt = _.compact(_.map(tokens, (token) => {
                        const expiresAt = token.get('expiresAt');
                        return expiresAt ? moment.utc(expiresAt) : expiresAt;
                    }));
                    if (_.size(allExpiresAt)) {
                        token.set('expiresAt', moment.max(allExpiresAt));
                    }

                    accountTokens[uniqKey] = token;

                    return accountTokens;
                }, {});

                return _.values(accountTokensByUniqKey);
            },

            disconnect: function (uniqueKeyPart) {
                const me = this;
                const channel = me.get('channel');

                if (!_.isUndefined(uniqueKeyPart)) {
                    // disconnect specific channel token
                    const key = me.getUniqueKey(channel);
                    const tokens = _.reject(RivalIQ.state.user.get(`${channel}TokenSummary`), function (token) {
                        return token[key] === uniqueKeyPart;
                    });
                    RivalIQ.state.user.set(`${channel}TokenSummary`, tokens);
                } else {
                    RivalIQ.state.user.unset(`${channel}TokenSummary`);
                }
            },

            hasMultiTokens: function (channel) {
                const details = _.find(enums.sourceDetails, {
                    channel: channel
                });
                return details.multiOauthToken;
            },

            getUniqueKey: function (channel) {
                const details = _.find(enums.sourceDetails, {
                    channel: channel
                });
                return details.uniqueKeyPart;
            },

            expiresAt: function () {
                const me = this;
                const channel = me.get('channel');

                // NOTE this message is for single token channels only
                return _.get(_.head(RivalIQ.state.user.get(`${channel}TokenSummary`)), 'expiresAt');
            },

            notHidden: function (skipCredentialCheck) {
                const me = this;

                if (skipCredentialCheck) {
                    return !RivalIQ.state.landscape.channelHidden(me.id);
                }

                return RivalIQ.state.landscape.channelAccess(me.id);
            },

            hasAccess: function () {
                const me = this;

                // TODO remove this when the beta is over
                if (me.id === enums.sources.TIKTOK_INSIGHTS && !RivalIQ.state.account.inBeta('tiktok_private')) {
                    return false;
                }

                return RivalIQ.state.landscape.channelAccess(me.id);
            },

            canDisplay: function () {
                const me = this;
                return RivalIQ.state.account.channelDisplay(me.id);
            },

            hasPlanAccess: function () {
                return true;
            },

            shouldAbbreviateTitle: function () {
                const me = this;
                return !_.includes([
                    enums.sources.CROSS_CHANNEL,
                    enums.sources.WEB
                ], me.id);
            },

            validCredentialsRequired: function () {
                const me = this;
                return Boolean(me.get('credentialsRequired'));
            },

            getTokenKey: function () {
                const me = this;
                return me.get('tokenKey') || me.getChannel();
            },

            filterCompanies: function (companyData) {
                const me = this;
                if (!me.get('companyFilter')) {
                    return companyData.toArray();
                }
                const companyIds = me.get('companyFilter')(companyData);

                return companyData.filter(function (company) {
                    return _.includes(companyIds, company.id);
                });
            },

            initialize: function (attrs) {
                const me = this;
                me.id = me.cid = attrs.id;
                me.attributes = attrs;
            }
        });

        const Channels = Backbone.Collection.extend({
            model: Channel,

            _isModel: function (model) {
                return model instanceof Channel;
            },

            // Make cross channel always first
            comparator: function (child) {
                return child.get('sortIndex');
            },

            get: function (id) {
                const me = this;

                if (id === 'seo') {
                    id = enums.sources.WEB;
                } else if (_.isString(id)) {
                    const channel = (_.has(enums.channels, id) ? enums.channels[id] : id);

                    if (_.isUndefined(channel)) {
                        id = enums.sources.CROSS_CHANNEL;
                    } else {
                        const details = _.find(enums.sourceDetails, {
                            channel: channel
                        });
                        id = _.get(details, 'source');
                    }
                } else if (_.isUndefined(id)) {
                    id = enums.sources.CROSS_CHANNEL;
                }

                return Backbone.Collection.prototype.get.call(me, id);
            },

            filter: function () {
                const me = this;
                const filter = Backbone.Collection.prototype.filter;
                return new Channels(filter.apply(me, arguments));
            },

            withAccess: function () {
                const me = this;
                return me.filter(function (channel) {
                    return channel.hasAccess();
                });
            },

            forDisplay: function () {
                const me = this;
                return me.filter(function (channel) {
                    return channel.canDisplay();
                });
            },

            supportedOnly: function () {
                const me = this;
                return me.filter(function (channel) {
                    return RivalIQ.state.account.channelSupported(channel.id);
                });
            },

            inCrossChannel: function () {
                const me = this;
                const crossChannelSubSources = data.channels.get().get('subSources');
                return me.filter(function (channel) {
                    return _.includes(crossChannelSubSources, channel.id);
                });
            },

            hideable: function () {
                const me = this;
                return me.filter(function (channel) {
                    return channel.get('hideable');
                });
            },

            notHidden: function (skipCredentialCheck) {
                const me = this;
                return me.filter(function (channel) {
                    return channel.notHidden(skipCredentialCheck);
                });
            },

            forAnalyze: function () {
                const me = this;
                return me.filter(function (channel) {
                    return !channel.get('dontShowInUi');
                });
            },

            getPlatformName: function (source) {
                // for private insights; get the original platform name
                const me = this;

                const channel = RivalIQ.enums.sourceDetails[source].channel;
                return me.get(channel).get('name');
            },

            // NOTE we need to set the secondary sources separately because
            // we need to filter out sources that may ONLY contain invalid
            // metrics for two dimensional visualizations (invalid meaning
            // marked as hidden, or text type metric)
            // TODO this could be more efficient
            withGraphableMetrics: function () {
                const me = this;
                return me.filter(function (channel) {
                    return Boolean(_.filter(RivalIQ.metrics.descriptions, function (metric) {
                        return (
                            channel.id === metric.source &&
                            !metric.dontShowInUi &&
                            metric.metricType !== RivalIQ.metrics.metricType.TEXT);
                    }).length);
                });
            }
        });

        const modelData = _.map(enums.sources, function (source) {
            return _.extend({
                id: source
            }, enums.sourceDetails[source]);
        });

        const channels = data.channels = new Channels(modelData);

        channels.withSearch = function () {
            const indexable = channels.filter('indexable');
            indexable.unshift(channels.get(enums.sources.CROSS_CHANNEL));
            return indexable;
        };

        /**
         * Returns channels that support authorization.
         */
        channels.withAuthorization = function () {
            const sources = [
                enums.sources.FACEBOOK,
                enums.sources.LINKEDIN,
                enums.sources.TWITTER
            ];

            if (RivalIQ.state.account.inBeta('tiktok_private') === true) {
                sources.push(enums.sources.TIKTOK);
            }

            const chans = _.map(sources, function (source) {
                return channels.get(source);
            });

            return new Channels(chans);
        };

        /**
         * Returns channels that support channel curation.
         */
        channels.withChannelCuration = function () {
            const sources = [
                enums.sources.CROSS_CHANNEL,
                enums.sources.TWITTER,
                enums.sources.FACEBOOK,
                enums.sources.YOUTUBE,
                enums.sources.INSTAGRAM,
                enums.sources.TIKTOK
            ];

            return new Channels(_.map(sources, _.bind(channels.get, channels)));
        };

        /**
         * Returns channels that have private post data
         */
        channels.withPrivatePostsData = function () {
            const sources = [
                enums.sources.TWITTER_INSIGHTS,
                enums.sources.FACEBOOK_INSIGHTS,
                enums.sources.INSTAGRAM_INSIGHTS,
                enums.sources.LINKEDIN
            ];

            // TODO remove when beta ends and add to array above
            if (RivalIQ.state.account.inBeta('tiktok_private') === true) {
                sources.push(enums.sources.TIKTOK_INSIGHTS);
            }

            return new Channels(_.map(sources, _.bind(channels.get, channels)));
        };

        /**
         * Returns visible channels for company identity (discoverable social).
         * @param {boolean} skipCheck
         */
        channels.forIdentity = function (skipCheck) {
            const chans = channels.filter('discoverable');
            return (skipCheck ? chans : chans.notHidden().withAccess());
        };
    }

})();
